import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import authService from '../../services/authService';
import { SupportLevel, levelRamp } from './supportMatrix';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Levels whose contacts all share one scope collapse to a single row with the
 * addresses stacked in one cell. A level whose contacts own different modules
 * gets one row each, with the level / contact / SLA cells spanning them.
 */
const LevelRows = ({ lvl, index }: { lvl: SupportLevel; index: number }) => {
  const { tone, wash } = levelRamp(index);
  const style = { ['--tone' as any]: tone, ['--wash' as any]: wash };
  const contacts = lvl.contacts || [];

  if (lvl.scope) {
    return (
      <tr className="sm-row-start" style={style}>
        <td className="sm-level"><span className="sm-badge">{lvl.level}</span></td>
        <td className="sm-contact">{lvl.role}</td>
        <td className="sm-emails">
          {contacts.map((contact, i) => (
            <React.Fragment key={contact.email}>
              {i > 0 ? <span className="sm-and">and</span> : null}
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </React.Fragment>
          ))}
        </td>
        <td className="sm-modules"><span className="sm-chip">{lvl.scope}</span></td>
        <td className="sm-sla">{lvl.sla}</td>
      </tr>
    );
  }

  const span = contacts.length;
  return (
    <>
      {contacts.map((contact, i) => (
        <tr key={contact.email} className={i === 0 ? 'sm-row-start' : undefined} style={style}>
          {i === 0 ? (
            <>
              <td className="sm-level" rowSpan={span}><span className="sm-badge">{lvl.level}</span></td>
              <td className="sm-contact" rowSpan={span}>{lvl.role}</td>
            </>
          ) : null}
          <td className="sm-emails">
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </td>
          <td className="sm-modules">{contact.modules}</td>
          {i === 0 ? <td className="sm-sla" rowSpan={span}>{lvl.sla}</td> : null}
        </tr>
      ))}
    </>
  );
};

/**
 * "Contact IT support" popup shown from the auth screens.
 * Renders the escalation matrix so a user who cannot sign in knows exactly
 * who to write to, and how long to wait before escalating.
 *
 * The matrix is loaded from the API the first time the popup is opened, then
 * kept for the life of the page — it changes rarely and every auth screen
 * shares this component.
 */
const SupportModal: React.FC<SupportModalProps> = ({ open, onClose }) => {
  const [levels, setLevels] = useState<SupportLevel[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const loadMatrix = useCallback(() => {
    setLoading(true);
    setErrorMsg('');
    authService
      .getEscalationMatrix()
      .then((data) => setLevels(data))
      .catch(() => setErrorMsg('The escalation matrix could not be loaded. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open && levels === null && !loading && !errorMsg) {
      loadMatrix();
    }
  }, [open, levels, loading, errorMsg, loadMatrix]);

  const hasRows = !!levels && levels.length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      centered
      className="support-modal"
      title={
        <div className="sm-head">
          <div className="sm-title">Escalation Matrix &amp; Helpdesk Communication Protocol</div>
          <div className="sm-sub">
            Raise every issue at L1 first. Escalate one level at a time, only after the
            acknowledgment window has passed without a response.
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="sm-state"><Spin /><span>Loading the escalation matrix…</span></div>
      ) : null}

      {!loading && errorMsg ? (
        <div className="sm-state">
          <span className="sm-state-error">{errorMsg}</span>
          <button type="button" className="login-link" onClick={loadMatrix}>Retry</button>
        </div>
      ) : null}

      {!loading && !errorMsg && !hasRows ? (
        <div className="sm-state"><span>No escalation contacts have been configured yet.</span></div>
      ) : null}

      {!loading && !errorMsg && hasRows ? (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th scope="col">Level</th>
                <th scope="col">Contact point</th>
                <th scope="col">Email / Support ID</th>
                <th scope="col">Modules</th>
                <th scope="col">SLA for acknowledgment</th>
              </tr>
            </thead>
            <tbody>
              {levels!.map((lvl, i) => <LevelRows key={lvl.level} lvl={lvl} index={i} />)}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="sm-foot">
        The SLA is the time to acknowledge your issue, not to resolve it.
      </div>
    </Modal>
  );
};

export default SupportModal;
